import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  CompanyBrandingRow,
  CompanyLogoAction,
  CompanyLogoMime,
  Database,
} from '../database.types';

/**
 * Writer wrapper for `save_company_branding` (migration 0016) — M10 / S10-03A.
 *
 * UI → this wrapper → Postgres RPC (ARCHITECTURE §2, INV-T02). **Nothing in
 * `src/` may write this table directly**, and that is enforced twice: the
 * table has no INSERT, UPDATE or DELETE policy, and a lint rule fails the build
 * on a direct mutation.
 *
 * **ONE CALL WRITES THE WHOLE PROFILE, WHICH IS WHAT MAKES REPLACEMENT SAFE.**
 * The client validates and re-encodes a new logo BEFORE calling; the write then
 * either commits the new mark or leaves the row exactly as it was. **There is
 * no moment in which the old logo has been cleared and the new one has not
 * arrived**, so a failed replacement can never leave Brandon with no logo.
 *
 * **`action` EXISTS BECAUSE A NULLABLE LOGO CANNOT SAY WHAT IT MEANS.** Null
 * would have to mean both "leave it alone" and "remove it" — and saving a phone
 * number would eventually delete a logo.
 */

export interface SaveCompanyBrandingInput {
  readonly companyName: string;
  readonly action: CompanyLogoAction;
  /** Present only when `action` is `replace`. */
  readonly logo?: { readonly mime: CompanyLogoMime; readonly base64: string } | undefined;
  readonly contactEmail: string | null;
  readonly contactPhone: string | null;
}

export type SaveCompanyBrandingResult =
  | { readonly ok: true; readonly row: CompanyBrandingRow }
  | { readonly ok: false; readonly message: string };

/** SQLSTATEs raised by the writer, mapped to sentences Brandon can act on. */
const NAME_REQUIRED = 'CL007';
const LOGO_INCOMPLETE = 'CL008';

export async function saveCompanyBranding(
  client: SupabaseClient<Database>,
  input: SaveCompanyBrandingInput,
): Promise<SaveCompanyBrandingResult> {
  const { data, error } = await client.rpc('save_company_branding', {
    p_company_name: input.companyName,
    p_logo_action: input.action,
    p_logo_mime: input.action === 'replace' ? (input.logo?.mime ?? null) : null,
    p_logo_base64: input.action === 'replace' ? (input.logo?.base64 ?? null) : null,
    p_contact_email: input.contactEmail,
    p_contact_phone: input.contactPhone,
  });

  if (error) {
    const code = (error as { code?: string }).code ?? '';
    const message = (error as { message?: string }).message ?? '';
    if (code === NAME_REQUIRED || message.includes(NAME_REQUIRED)) {
      return { ok: false, message: 'Your company name is needed before branding can be saved.' };
    }
    if (code === LOGO_INCOMPLETE || message.includes(LOGO_INCOMPLETE)) {
      return { ok: false, message: 'That logo could not be saved. Please choose it again.' };
    }
    // **THE DATABASE'S OWN CONSTRAINTS ARE THE AUTHORITY**, not the browser's
    // opinion of a file. A refusal from one of them is reported as a refusal,
    // never swallowed into a success.
    return { ok: false, message: 'Your company branding could not be saved. Please try again.' };
  }

  if (!data) return { ok: false, message: 'Your company branding could not be saved.' };
  return { ok: true, row: data };
}
